import { Shell } from '../components';
import { webConfig } from '../config';

const questions = [
  {
    question: '家族と作品や声を共有するには？',
    answer: (
      <>
        <p>
          家族を招待する方は、ログイン後に「設定」→「家族を招待」で8文字の招待コードを発行します。
          参加する方は、ご自身のAppleまたはGoogleアカウントでログインし、「設定」→「招待コードで参加する」からコードを入力してください。初回起動の案内からも参加できます。
        </p>
        <p>
          コードは7日間有効で、1人につき1つ必要です。参加後は、同じ家族の作品や声が自動で同期されます。
        </p>
      </>
    ),
  },
  {
    question: '作品や声が別の端末に表示されません',
    answer: (
      <p>
        両方の端末がインターネットにつながり、同じ家族に参加しているか確認してください。
        「設定」→「保存と家族の同期」で保存・同期の状態を確認できます。
        送信が終わるまではアプリを開いてお待ちください。ログインせずに保存したミュージアムやことば帳の作品は、その端末に保存されます。
      </p>
    ),
  },
  {
    question: '写真を選べない・声を録音できないときは？',
    answer: (
      <p>
        端末の「設定」から「うちのこばこ」を開き、写真やマイクへのアクセスを確認してください。
        写真へのアクセスを一部だけ許可している場合は、使いたい写真を追加します。
        録音した声が聞こえない場合は、再生中の音量とBluetoothイヤホンなどの接続先も確認してください。
      </p>
    ),
  },
  {
    question: '購入したのにPremiumが使えません',
    answer: (
      <p>
        購入時と同じAppleまたはGoogleのストアアカウントを使っているか確認し、
        アプリの「設定」→「うちのこばこ Premium」から「購入を復元」をお試しください。
        アプリへのログインにも、購入時に使ったアカウントをご利用ください。
        解決しない場合は、再購入せずお問い合わせください。
      </p>
    ),
  },
  {
    question: 'Premiumを解約するには？',
    answer: (
      <>
        <p>
          購入したストアで自動更新を停止できます。アプリやアカウントの削除だけでは解約されません。
          ご契約中は、アプリのPremium画面の「プランの変更・解約」からも手続きに進めます。
        </p>
        <div className="support-answer-links">
          <a href="https://support.apple.com/ja-jp/118428">App Storeでの解約方法</a>
          <a href="https://support.google.com/googleplay/answer/7018481?hl=ja">
            Google Playでの解約方法
          </a>
        </div>
      </>
    ),
  },
  {
    question: '機種変更するときはどうすればいいですか？',
    answer: (
      <p>
        元の端末でログインし、「設定」→「保存と家族の同期」で送信が完了していることを確認してください。
        新しい端末で同じアカウントにログインすると、サーバーに保存された作品や声を読み込めます。
        ログイン前や送信前のデータは端末に残っているため、新しい端末で確認できるまでは元のアプリを削除しないでください。
      </p>
    ),
  },
  {
    question: 'アカウントや作品を削除するには？',
    answer: (
      <>
        <p>
          作品はそれぞれの作品画面から、アカウントは「設定」→「データの削除」→「アカウントを削除」から削除できます。
          「世帯と家族データを削除」は、家族全員のデータが対象です。削除は取り消せないため、画面の案内をご確認ください。
        </p>
        <p>アプリを開けないなど、お手続きが難しい場合はメールでご相談ください。</p>
      </>
    ),
  },
];

export function SupportPage() {
  const email = webConfig.supportEmail;
  const contactHref = email
    ? `mailto:${email}?subject=${encodeURIComponent('うちのこばこについてのお問い合わせ')}`
    : undefined;

  return (
    <Shell>
      <title>サポート・お問い合わせ｜うちのこばこ</title>
      <main className="support-page">
        <header className="support-heading">
          <p className="support-label">うちのこばこ お問い合わせ窓口</p>
          <h1>サポート</h1>
          <p>使い方やお困りごと、ご要望をお聞かせください。</p>
        </header>

        <section className="support-contact" aria-labelledby="contact-title">
          <div>
            <h2 id="contact-title">メールでお問い合わせ</h2>
            <p>
              お困りの状況と、端末の機種・OS・アプリのバージョンをお知らせいただくとスムーズです。
            </p>
          </div>
          <div className="support-contact-action">
            {contactHref ? (
              <>
                <a className="button support-email-button" href={contactHref}>
                  メールを作成する
                </a>
                <span className="support-email-address">{email}</span>
              </>
            ) : (
              <p>お問い合わせ先を準備中です。</p>
            )}
          </div>
        </section>

        <section className="support-faq" aria-labelledby="faq-title">
          <h2 id="faq-title">よくあるご質問</h2>
          <div className="support-questions">
            {questions.map(({ question, answer }) => (
              <details key={question}>
                <summary>{question}</summary>
                <div className="support-answer">{answer}</div>
              </details>
            ))}
          </div>
          <a className="support-guide-link" href="/print">
            表紙の印刷については、印刷ガイドへ
          </a>
        </section>

        <section className="support-operator" aria-labelledby="operator-title">
          <h2 id="operator-title">運営者情報</h2>
          <dl>
            <div>
              <dt>運営者</dt>
              <dd>{webConfig.legalOperatorName ?? '準備中'}</dd>
            </div>
            <div>
              <dt>所在地</dt>
              <dd>{webConfig.legalAddress ?? '準備中'}</dd>
            </div>
            <div>
              <dt>メール</dt>
              <dd>{email ? <a href={contactHref}>{email}</a> : '準備中'}</dd>
            </div>
          </dl>
          <nav className="support-legal-links" aria-label="サポートの関連情報">
            <a href="/privacy">プライバシーポリシー</a>
            <a href="/terms">利用規約</a>
          </nav>
        </section>
      </main>
    </Shell>
  );
}
